
import React, { useState, useMemo, useEffect } from 'react';
import type { ModificationLevel, GenerationModel, GenerationJob } from '../types';
import { curriculum } from '../curriculumData';

interface CreativeGenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestGeneration: (jobDetails: {
    baseProblem?: undefined;
    modificationLevel: ModificationLevel;
    generationModel: GenerationModel;
    useThinkingMode?: boolean;
    curriculumPath: string;
    difficulty?: string;
    numberOfProblems: number;
    folder?: string;
  }) => Promise<void>;
  jobToRetry: GenerationJob | null;
  allFolderPaths: string[];
}

const CreativeGenerateModal: React.FC<CreativeGenerateModalProps> = ({ isOpen, onClose, onRequestGeneration, jobToRetry, allFolderPaths }) => {
  const [generationModel, setGenerationModel] = useState<GenerationModel>('gemini-2.5-pro');
  const [useThinkingMode, setUseThinkingMode] = useState<boolean>(true);
  const [difficulty, setDifficulty] = useState<string | null>(null);
  const [numberOfProblems, setNumberOfProblems] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Curriculum state
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedMajorUnit, setSelectedMajorUnit] = useState<string>('');
  const [selectedSubUnit, setSelectedSubUnit] = useState<string>('');
  const [selectedFolder, setSelectedFolder] = useState<string>('');

  const majorUnits = useMemo(() => selectedSubject ? Object.keys(curriculum[selectedSubject]) : [], [selectedSubject]);
  const subUnits = useMemo(() => (selectedSubject && selectedMajorUnit) ? curriculum[selectedSubject][selectedMajorUnit] : [], [selectedSubject, selectedMajorUnit]);

  useEffect(() => {
    if (isOpen) {
      if (jobToRetry && jobToRetry.modificationLevel === 4) {
        setIsRetrying(true);
        // Safe splitting with fallback
        const [subject, major, sub] = (jobToRetry.curriculumPath || '').split('.');
        setSelectedSubject(subject || '');
        setSelectedMajorUnit(major || '');
        setSelectedSubUnit(sub || '');
        setDifficulty(jobToRetry.difficulty || null);
        setGenerationModel(jobToRetry.generationModel || 'gemini-2.5-pro');
        setUseThinkingMode(jobToRetry.useThinkingMode || false);
        setNumberOfProblems(jobToRetry.numberOfProblems || 1);
        setSelectedFolder(jobToRetry.folder || '');
      } else {
        setIsRetrying(false);
      }
    }
  }, [jobToRetry, isOpen]);


  const handleGenerateRequest = async () => {
    if (!selectedSubject || !selectedMajorUnit || !selectedSubUnit) {
      setError('문제의 단원을 모두 선택해주세요.');
      return;
    }
    if (!difficulty) {
      setError('문제의 난이도를 선택해주세요.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const curriculumPath = `${selectedSubject}.${selectedMajorUnit}.${selectedSubUnit}`;
      const folderName = selectedFolder || `${selectedSubUnit}-창작`;
      await onRequestGeneration({
        modificationLevel: 4,
        generationModel,
        useThinkingMode,
        curriculumPath,
        difficulty,
        numberOfProblems,
        folder: folderName,
      });
      setRequestSubmitted(true);
    } catch (err) {
      if (err instanceof Error) setError(`요청 제출 중 오류: ${err.message}`);
      else setError('요청 제출 중 알 수 없는 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
      if(isSubmitting) return;
      setGenerationModel('gemini-2.5-pro');
      setUseThinkingMode(true);
      setDifficulty(null);
      setNumberOfProblems(1);
      setError('');
      setRequestSubmitted(false);
      setSelectedSubject('');
      setSelectedMajorUnit('');
      setSelectedSubUnit('');
      setSelectedFolder('');
      setIsRetrying(false);
      onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleClose]);
  
  if (!isOpen) return null;

  const selectClasses = "w-full bg-black/20 border border-[var(--color-border-glass)] rounded-md py-2 px-3 text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]";
  const difficultyButtonClass = (level: string) => `w-full text-left p-3 rounded-md transition-all duration-200 border-2 ${difficulty === level ? 'bg-[var(--color-accent)] border-[var(--color-accent-hover)] text-[var(--color-accent-text)]' : 'bg-black/20 border-[var(--color-border-glass)] text-[var(--color-text-primary)] hover:bg-white/10'}`;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4" onClick={handleClose}>
        <div className="bg-[var(--color-bg-glass)] border border-[var(--color-border-glass)] rounded-lg shadow-xl w-full max-w-2xl p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">&times;</button>
            <h2 className="text-2xl font-bold text-[var(--color-accent)] mb-4">{isRetrying ? '작업 재시도' : '창작문제 만들기'}</h2>

             {requestSubmitted ? (
                 <div className="text-center py-12">
                    <div className="text-green-400 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-[var(--color-text-primary)] mb-2">요청 접수 완료!</h3>
                    <p className="text-[var(--color-text-secondary)]">
                        '작업 관리자'에서 실시간 제련 과정을 확인하세요.
                    </p>
                    <button onClick={handleClose} className="mt-6 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-[var(--color-accent-text)] font-bold py-2 px-6 rounded-lg">닫기</button>
                </div>
            ) : (
                <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                    <div>
                        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-3">1. 단원 선택</h3>
                        <div className="space-y-3">
                            <select value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setSelectedMajorUnit(''); setSelectedSubUnit(''); }} className={selectClasses}>
                                <option value="">과목 선택...</option>
                                {Object.keys(curriculum).map(subject => <option key={subject} value={subject}>{subject}</option>)}
                            </select>
                            <select value={selectedMajorUnit} onChange={e => { setSelectedMajorUnit(e.target.value); setSelectedSubUnit(''); }} className={selectClasses} disabled={!selectedSubject}>
                                <option value="">대단원 선택...</option>
                                {majorUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                            </select>
                            <select value={selectedSubUnit} onChange={e => setSelectedSubUnit(e.target.value)} className={selectClasses} disabled={!selectedMajorUnit}>
                                <option value="">소단원 선택...</option>
                                {subUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-3">2. 난이도 선택</h3>
                        <div className="space-y-2">
                             {(
                                [
                                { key: '3점', title: '3점', desc: '개념 이해를 확인하는 수준의 문제입니다.' },
                                { key: '쉬운 4점', title: '쉬운 4점', desc: '두 가지 이상의 개념이 결합된 문제입니다.' },
                                { key: '어려운 4점', title: '어려운 4점', desc: '새로운 접근법이나 깊은 사고를 요구하는 문제입니다.' },
                                { key: '킬러 (22번/30번 급)', title: '킬러 (최고난도)', desc: '23~25 수능 22번, 30번 급의 초고난도 문제입니다.' }
                                ]
                            ).map(({key, title, desc}) => (
                                <button key={key} onClick={() => setDifficulty(key)} className={difficultyButtonClass(key)}>
                                <p className="font-bold">{title}</p>
                                <p className="text-sm">{desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-3">3. 생성 모드 선택</h3>
                        <div className="flex flex-col sm:flex-row bg-black/20 rounded-lg p-1 border border-[var(--color-border-glass)] gap-1">
                            <button onClick={() => { setGenerationModel('gemini-2.5-flash'); setUseThinkingMode(false); }} className={`flex-1 text-center p-2 rounded-md transition-all duration-200 ${generationModel === 'gemini-2.5-flash' && !useThinkingMode ? 'bg-blue-500 text-white' : 'text-[var(--color-text-secondary)] hover:bg-white/10'}`}>
                                <p className="font-bold">빠른 생성</p><p className="text-xs">(Flash)</p>
                            </button>
                            <button onClick={() => { setGenerationModel('gemini-2.5-pro'); setUseThinkingMode(false); }} className={`flex-1 text-center p-2 rounded-md transition-all duration-200 ${generationModel === 'gemini-2.5-pro' && !useThinkingMode ? 'bg-purple-600 text-white' : 'text-[var(--color-text-secondary)] hover:bg-white/10'}`}>
                                <p className="font-bold">고품질 생성</p><p className="text-xs">(Pro)</p>
                            </button>
                            <button onClick={() => { setGenerationModel('gemini-2.5-pro'); setUseThinkingMode(true); }} className={`flex-1 text-center p-2 rounded-md transition-all duration-200 ${useThinkingMode ? 'bg-amber-500 text-black shadow-[0_0_15px_rgba(245,158,11,0.7)]' : 'text-[var(--color-text-secondary)] hover:bg-white/10'}`}>
                                <p className="font-bold">초고퀄리티</p><p className="text-xs">(Deep Think)</p>
                            </button>
                            <button onClick={() => { setGenerationModel('gemini-3-pro-preview'); setUseThinkingMode(false); }} className={`flex-1 text-center p-2 rounded-md transition-all duration-200 ${generationModel === 'gemini-3-pro-preview' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-[var(--color-text-secondary)] hover:bg-white/10'}`}>
                                <p className="font-bold">차세대 모델</p><p className="text-xs">(V3 Pro)</p>
                            </button>
                        </div>
                    </div>
                     <div>
                        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-3">4. 저장 위치 (선택 사항)</h3>
                        <select value={selectedFolder} onChange={e => setSelectedFolder(e.target.value)} className={selectClasses}>
                            <option value="">단원명으로 자동 생성</option>
                            {allFolderPaths.map(path => <option key={path} value={path}>{path}</option>)}
                        </select>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-3">5. 제작할 문항 개수</h3>
                        <input type="number" min="1" max="10" value={numberOfProblems} onChange={(e) => setNumberOfProblems(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))} className={selectClasses} />
                    </div>
                     <div className="mt-6 flex justify-end">
                        <button onClick={handleGenerateRequest} disabled={!selectedSubUnit || !difficulty || isSubmitting} className="w-full bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-[var(--color-accent-text)] font-bold py-3 px-4 rounded-lg transition-colors duration-300 disabled:bg-[var(--color-text-tertiary)] disabled:cursor-not-allowed">
                            {isSubmitting ? '요청 중...' : `최고의 컨텐츠 출정 요청 (${numberOfProblems}개)`}
                        </button>
                    </div>
                    {error && <p className="text-rose-400 text-sm mt-2 text-center">{error}</p>}
                </div>
            )}
        </div>
    </div>
  );
};

export default CreativeGenerateModal;